import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import type { CSSProperties } from "react";
import { CONTROL_HEIGHT_PX, INK, LOCATION_LADDER, LOCATION_STAFFING, MOTION, NEUTRAL, SURFACE } from "@/theme/tokens";
import type { NurseRole, RatioLadder } from "@/utils/models";

/**
 * Which markup a location-editor section renders. Each section switches over this with a `ReactElement`
 * return, so a new design fails to compile until every section renders it.
 *
 * - `v1`: the Schedule Template popup, unchanged.
 * - `v2`: the Locations page's calm form (APP-780) — compact steppers and round day chips instead of number
 *   fields and day pills, uppercase labels over the fields. Same props and handlers as v1; only the markup differs.
 */
export type LocationEditorDesign = "v1" | "v2";

/**
 * The sections only the Schedule Template popup renders take only this: the full identity fields (section and
 * name), the delete control and the action bar. The Locations page owns a location's name, its footer and its
 * delete itself; its expanded editor renders just the section, through `LocationIdentityFields`' v2 variant.
 */
export type V1OnlyLocationEditorDesign = Extract<LocationEditorDesign, "v1">;

/** The popup's small field labels and helper lines. */
export const smallLabelTypographySx: SxProps = {
	fontWeight: 500,
	fontStyle: "normal",
	fontSize: "12px",
	lineHeight: "14px",
	letterSpacing: "0px",
	verticalAlign: "middle",
	color: alpha(INK.SECONDARY, 0.6),
} as const satisfies SxProps;

// --- v1 geometry, moved verbatim out of the editor's markup (APP-780 PR1). Same values; not a tokenization. ---

/** The form's own inset. Shared so the pinned action bar can reach across it — see its `bottom`. */
export const V1_SCROLL_CONTAINER_PADDING_PX = 8;
export const V1_REGULAR_FIELD_WIDTH = "160px" as const satisfies CSSProperties["width"];
export const V1_SMALL_NUMERIC_FIELD_WIDTH = "100px" as const satisfies CSSProperties["width"];
export const V1_CTA_BUTTON_WIDTH = "100px" as const satisfies CSSProperties["width"];
export const V1_JOB_SELECTOR_WIDTH = "200px" as const satisfies CSSProperties["width"];
/** The widest a title or helper line runs before it ellipsizes or wraps. */
export const V1_TEXT_MAX_WIDTH = "500px" as const satisfies CSSProperties["maxWidth"];
export const V1_TITLE_FONT_SIZE = "26px" as const satisfies CSSProperties["fontSize"];
/** The hours box, its popover and the affect-date box. */
export const V1_FIELD_BORDER_RADIUS = "4px" as const satisfies CSSProperties["borderRadius"];
export const V1_HOURS_FONT_SIZE = "13px" as const satisfies CSSProperties["fontSize"];
export const V1_APPLY_FROM_FONT_SIZE = "15px" as const satisfies CSSProperties["fontSize"];
export const V1_APPLY_FROM_PADDING = "2px" as const satisfies CSSProperties["padding"];
export const V1_LADDER_COMPACT_FIELD_WIDTH = "75px" as const satisfies CSSProperties["width"];
/** Wider than its siblings: it also holds the "no upper limit" button. */
export const V1_LADDER_MAX_FIELD_WIDTH = "108px" as const satisfies CSSProperties["width"];
/** The red outline drawn around a ladder rung's job, roles or days when they're missing. */
export const V1_ERROR_OUTLINE_RADIUS = "8px" as const satisfies CSSProperties["borderRadius"];
export const V1_ERROR_OUTLINE_PADDING = "2px" as const satisfies CSSProperties["padding"];
/** The job colour dot before a ladder rung's job name. `!important` beats the Box's own sizing. */
export const V1_JOB_DOT_SIZE = "10px !important" as const satisfies CSSProperties["width"];
/** Between the day buttons of a staffing-target row's and a ladder rung's days picker. */
export const V1_DAYS_PICKER_GAP = "2px" as const satisfies CSSProperties["gap"];

export const regularFieldSx: SxProps = {
	width: V1_REGULAR_FIELD_WIDTH,
} as const satisfies SxProps;

/**
 * Height reveal for the blocks that appear once an out-of-config edit starts moving real shifts.
 * Collapse nests children in plain divs, so the inner wrapper has to re-establish the column layout
 * they had as direct flex items of the dialog.
 */
export const revealSx: SxProps<Theme> = {
	width: "100%",
	// The dialog is a height-capped scrolling flex column, and Collapse's root sets `min-height: 0`, so without this the
	// column shrinks the open block below its content instead of scrolling — the apply-from date then spills over Delete.
	flexShrink: 0,
	transitionTimingFunction: MOTION.EASE_OUT,
	"& .MuiCollapse-wrapperInner": {
		display: "flex",
		flexDirection: "column",
		gap: 2,
		alignItems: "start",
	},
	"@media (prefers-reduced-motion: reduce)": { transitionDuration: "0ms" },
};

// --- v2 geometry (APP-780): the Locations page's form. Built from theme tokens; named here so the sections agree. ---

/**
 * The highest a v2 staff-count stepper (Target, Required) goes. The stepper's value column is sized for two
 * digits (`STEPPER.VALUE_MIN_WIDTH`), and no single shift row staffs more than that.
 */
export const V2_STAFF_COUNT_MAX = 99;

/** A v2 panel's border (px). Named so a layout that adds up a panel's width counts it. */
export const V2_PANEL_BORDER_PX = 1;

/** The bordered panel the Locations page's editor lays its fields out in (`ExpandedEditorFrame`). */
export const v2PanelSx: SystemStyleObject<Theme> = {
	border: `${V2_PANEL_BORDER_PX}px solid ${NEUTRAL[200]}`,
	borderRadius: 1,
	bgcolor: SURFACE,
	width: "100%",
	minWidth: 0,
};

/** The hairline between two lines of a v2 panel. */
export const V2_PANEL_HAIRLINE = `1px solid ${NEUTRAL[150]}`;

/**
 * One line of a v2 settings panel (a `SettingRow`): its own vertical padding, and the hairline that parts it from
 * whatever comes before it in the panel. The first line has no hairline, so the panel's border is the only edge
 * above it.
 */
export const v2SettingRowSx: SystemStyleObject<Theme> = {
	py: 1.25,
	"* + &": { borderTop: V2_PANEL_HAIRLINE },
};

/** The v2 apply-from date: four control heights, room for a medium date ("Sep 27, 2026") beside the calendar icon. */
export const V2_APPLY_FROM_FIELD_WIDTH = `${CONTROL_HEIGHT_PX.STANDARD * 4}px` as const satisfies CSSProperties["width"];

/** The ladder section's container: its steps follow the width the section has, not the window's. */
export const V2_LADDER_CONTAINER = "ratioLadder";
/**
 * How long (ms) a ratio ladder (v1 and v2) waits after the last edit before it re-sorts its steps by "Census from". Sorting on
 * every keystroke moved the step being typed in out from under the admin (clearing one digit is enough to make it
 * sort elsewhere); two quiet seconds means they have stopped typing.
 */
export const LADDER_REORDER_DELAY_MS = 2000;

/**
 * The narrowest the ladder section can be with each step on the design's one line: the number's column, the three
 * fields, the remove button and the four gaps between those five columns.
 */
const V2_LADDER_ONE_LINE_MIN_PX =
	LOCATION_STAFFING.STEP_NUMBER_COLUMN +
	LOCATION_STAFFING.STEP_FROM_WIDTH +
	LOCATION_STAFFING.STEP_UP_TO_WIDTH +
	LOCATION_STAFFING.STEP_NEEDED_WIDTH +
	CONTROL_HEIGHT_PX.COMPACT +
	4 * LOCATION_STAFFING.STEP_GAP_X;
/**
 * Where a step's fields share a narrow line (a phone), Up to's share of it against Census from's 1: it also holds the
 * ∞ button, so at an even split a three-digit census wouldn't fit beside it.
 */
export const V2_LADDER_NARROW_UP_TO_FR = 1.3;
export const V2_LADDER_ONE_LINE = `@container ${V2_LADDER_CONTAINER} (min-width: ${V2_LADDER_ONE_LINE_MIN_PX}px)`;

/** A ladder's body (its steps, "Add a step" and its issues): the issues go beside the steps where it has the room. */
export const V2_LADDER_BODY_CONTAINER = "ratioLadderBody";
/**
 * Where a ladder's body fits its steps on their one line and an issues column beside them, what's wrong with the
 * ladder and its steps sits in that column instead of above and under the steps, so a problem appearing or going away
 * never moves them. Narrower, the problem stays above the steps and each step's error under it.
 */
export const V2_LADDER_SIDE_ISSUES = `@container ${V2_LADDER_BODY_CONTAINER} (min-width: ${V2_LADDER_ONE_LINE_MIN_PX + LOCATION_LADDER.ISSUES_GAP_X + LOCATION_LADDER.ISSUES_MIN_WIDTH}px)`;

/**
 * The staffing sections' "Add days with different targets" / "Add a step": plain text until hovered. A borderless
 * button fills with the page surface, which on a tinted card would read as a white chip.
 */
export const V2_ADD_ROW_BUTTON_SX: SystemStyleObject<Theme> = { backgroundColor: "transparent" };

/**
 * The census area's Unit and Floor dropdowns when they share one line with Department wide and Rooms (`layout="row"`):
 * wide enough for an EMR unit or floor name such as "2 West" or "Floor 3" beside the chevron. Past it the name ellipsizes.
 */
export const V2_EMR_AREA_FIELD_WIDTH = `${CONTROL_HEIGHT_PX.STANDARD * 4.5}px` as const satisfies CSSProperties["width"];
/** The narrowest the Rooms dropdown runs on that line (it grows into what's left): room for two chips. Narrower, it wraps to its own line. */
export const V2_EMR_ROOMS_FIELD_MIN_WIDTH = `${CONTROL_HEIGHT_PX.STANDARD * 6}px` as const satisfies CSSProperties["minWidth"];

/**
 * One ratio-ladder rung as its row markup sees it. `RatioLadderSection` owns the ladder and every handler;
 * it derives these flags and binds the callbacks to the rung, so a row only renders.
 */
export type RatioLadderRowProps = {
	ratio: RatioLadder;
	locationRoles: NurseRole[];
	showRoleChips: boolean;
	showJobChips: boolean;
	jobDisplayName: string | undefined;
	hasRolesError: boolean;
	hasJobsError: boolean;
	hasAmountError: boolean;
	hasDaysError: boolean;
	hasRangeError: boolean;
	hasCensusConflict: boolean;
	/** Days this rung's census range overlaps another rung on; undefined when it overlaps none. */
	conflictingDays: Set<string> | undefined;
	isOpenEnded: boolean;
	autoFocusMax: boolean;
	/** The last rung, while the ladder can still take another one. */
	showAddButton: boolean;
	/** Every rung but the last. */
	showDivider: boolean;
	/** The rung's place in the ladder, from 1 at the lowest census. */
	stepNumber: number;
	onUpdate: (updates: Partial<RatioLadder>) => void;
	/** What the admin typed into Min, unparsed. */
	onMinTyped: (typed: string) => void;
	/** What the admin typed into Amount, unparsed. */
	onAmountTyped: (typed: string) => void;
	/** What the admin typed into Max, unparsed. */
	onMaxTyped: (typed: string) => void;
	onMaxUnbounded: () => void;
	onMaxFocus: () => void;
	onMaxBlur: () => void;
	onDelete: () => void;
	onAdd: () => void;
};
